import { useState, type ReactNode } from "react";
import { useRoleSwitch } from "../auth/useRoleSwitch";
import { RoleMenu } from "./RoleMenu";
import css from "./Header.module.css";

/**
 * App-wide header per B.2.2 + B.2.8.
 *
 * Edge-to-edge — --surface-1 background, no horizontal padding on the
 * container itself. Two stacked rows in the left column:
 *
 *   Row 1 — wordmark "UniBooking" (gradient clip-text)
 *   Row 2 — HUD role-badge with clip-corner + pulse-corners + scanline.
 *           Contents: `{roleHudLabel} | {nickname}`. The pipe is a vertical
 *           role-coloured line inside the plaque. Both pieces use the same
 *           Orbitron 8.5px caps face (Latin-only — Cyrillic in this slot
 *           silently falls back to Chakra Petch and looks off).
 *
 * Right slot — caller-provided M.1 DateSelector trigger.
 *
 * Role-color comes from `var(--role)` set by <html data-role="…">.
 */
interface HeaderProps {
  /** Display nickname (Telegram first_name / staff.nickname). Goes INSIDE the badge. */
  userName: string;
  /**
   * Short Latin-only label for the HUD slot, e.g.:
   *  DC  → "DEP. OF COMMERCE"
   *  OO  → "OFFICE OPERATIONS"
   *  EO  → "EXECUTIVE OFFICE"
   *  OP  → "OPERATOR"
   */
  roleLabel: string;
  /** Right-side slot — pass the M.1 <DateSelector /> trigger here. */
  dateSelectorSlot?: ReactNode;
  /**
   * Nickname placement (the two approved references differ):
   *  - false (default) — nick INSIDE the role-badge: `{ROLE} | {ник}`.
   *    Matches DC screen-02 cross-branch view (B.2.8).
   *  - true — nick in row 1 next to the wordmark: `UniBooking · {ник}`,
   *    badge shows only the role label. Matches OO screen-12 (B.12.4).
   */
  nickInWordmark?: boolean;
}

export function Header({ userName, roleLabel, dateSelectorSlot, nickInWordmark = false }: HeaderProps) {
  const { canSwitch } = useRoleSwitch();
  const [roleMenu, setRoleMenu] = useState(false);
  return (
    <header className={css.appHead}>
      <div className={css.left}>
        <div className={css.wmRow}>
          <span className={css.wm}>UniBooking</span>
          {nickInWordmark ? (
            <>
              <span className={css.wmSep} aria-hidden="true">·</span>
              <span className={css.wmUser}>{userName}</span>
            </>
          ) : null}
        </div>
        <div className={css.roleAnchor}>
          <div
            className={`${css.roleBadge} ${canSwitch ? css.clickable : ""}`}
            role={canSwitch ? "button" : undefined}
            tabIndex={canSwitch ? 0 : undefined}
            aria-haspopup={canSwitch ? "menu" : undefined}
            aria-expanded={canSwitch ? roleMenu : undefined}
            onClick={canSwitch ? () => setRoleMenu((o) => !o) : undefined}
          >
            <span className={`${css.rbCorner} ${css.rbCornerTl}`} aria-hidden="true" />
            <span className={`${css.rbCorner} ${css.rbCornerBr}`} aria-hidden="true" />
            <span className={css.rbText}>{roleLabel}</span>
            {nickInWordmark ? null : (
              <>
                <span className={css.rbPipe} aria-hidden="true" />
                <span className={css.rbUser}>{userName}</span>
              </>
            )}
            {canSwitch ? <span className={css.rbChev} aria-hidden="true">▾</span> : null}
          </div>
          {canSwitch ? <RoleMenu open={roleMenu} onClose={() => setRoleMenu(false)} /> : null}
        </div>
      </div>
      {dateSelectorSlot ? <div className={css.right}>{dateSelectorSlot}</div> : null}
    </header>
  );
}
